/**
 * Where every number on the screen came from.
 *
 * Turn the overlay on and each value picks up a small label — `T8.successes`,
 * `T12.started_at` — naming the table and column it was read from. The `T`
 * numbers are the ones printed on every table in `research/data-model.html`,
 * so a label on screen can be looked up in the diagram without knowing or
 * caring what the real table is called.
 *
 * It is a checking tool, not a feature. It is off by default, it is never
 * shown to a child, and the plan is to delete it once the data model has been
 * checked against the screens once. Until then it is the cheapest way to
 * answer "where does this come from" without reading the code.
 *
 * **Turning it on**: Settings, Your account, "Show where data is stored".
 * Or add `?trace=1` to the address, or press `Ctrl` + `Alt` + `T`. The
 * choice is remembered in the browser until it is turned off again. (Added
 * the Settings switch on 8 Oct 2026: Sam had been told about the key combo,
 * and on his keyboard it never did anything visible.)
 *
 * **Two kinds of label** since 8 Oct 2026. A dashed one is data the screen
 * READ from a table. A solid one with an arrow, `→ T14.response`, is where
 * what you type or tap is SAVED. Before, only reads were labelled, so a form
 * or a question showed nothing at all, which is the half Sam asked about.
 *
 * **Click a label** to open the data map at that table (laptop only; the
 * map is copied in by run_local.py).
 *
 * **Adding a label**: wrap the value.
 *
 *     <Trace id="T8.successes">{state.successes}</Trace>
 *
 * A label whose table or column does not exist shows in red rather than
 * failing quietly, so a typo or a renamed column is visible on the screen
 * rather than being a label that silently lies.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

// ---------------------------------------------------------------- the tables

/** The six groups the data model lays its tables out in. */
export const GROUPS = {
  id: { label: 'Identity', colour: '#6B5BD2' },
  voc: { label: 'Vocabulary', colour: '#2563EB' },
  mast: { label: 'Mastery & scheduling', colour: '#B8541F' },
  cont: { label: 'Content', colour: '#1D6B38' },
  sess: { label: 'Sessions & practice', colour: '#B02A2A' },
  extra: { label: 'Interests, badges, settings', colour: '#8A6508' },
} as const

export type GroupKey = keyof typeof GROUPS

/**
 * T1 to T35, as `research/data-model.html` prints them (T31 and T32, the
 * device-code tables of 22 Oct 2026, T33 placement_check and T34
 * parental_consent of 29 Oct 2026, and T35 rate_event of 2 Nov 2026, are not
 * drawn there yet).
 *
 * Kept in the same order as the diagram so the two can be read side by side.
 * If a table is added there, add it here; the mismatch check below will point
 * at any label that names a table this list has never heard of.
 */
export const TABLES: Record<string, { table: string; group: GroupKey }> = {
  T1: { table: 'parent', group: 'id' },
  T2: { table: 'learner', group: 'id' },
  T3: { table: 'word', group: 'voc' },
  T4: { table: 'word_sense', group: 'voc' },
  T5: { table: 'word_list', group: 'voc' },
  T6: { table: 'word_list_entry', group: 'voc' },
  T7: { table: 'learner_word', group: 'voc' },
  T8: { table: 'mastery_state', group: 'mast' },
  T9: { table: 'item', group: 'cont' },
  T10: { table: 'item_version', group: 'cont' },
  T11: { table: 'content_flag', group: 'cont' },
  T12: { table: 'session', group: 'sess' },
  T13: { table: 'session_plan_entry', group: 'sess' },
  T14: { table: 'trial', group: 'sess' },
  T15: { table: 'question_history', group: 'sess' },
  T16: { table: 'interest', group: 'extra' },
  T17: { table: 'learner_interest', group: 'extra' },
  T18: { table: 'badge', group: 'extra' },
  T19: { table: 'learner_badge', group: 'extra' },
  T20: { table: 'setting', group: 'extra' },
  T21: { table: 'schema_migration', group: 'extra' },
  T22: { table: 'word_request', group: 'voc' },
  T23: { table: 'book', group: 'voc' },
  T24: { table: 'learner_photo', group: 'id' },
  T25: { table: 'learner_pin', group: 'id' },
  T26: { table: 'parent_billing', group: 'id' },
  T27: { table: 'parent_pin', group: 'id' },
  T28: { table: 'access_grant', group: 'id' },
  T29: { table: 'book_use', group: 'voc' },
  T30: { table: 'auth_session', group: 'id' },
  T31: { table: 'device_code', group: 'id' },
  T32: { table: 'device_code_miss', group: 'id' },
  T33: { table: 'placement_check', group: 'voc' },
  T34: { table: 'parental_consent', group: 'id' },
  T35: { table: 'rate_event', group: 'id' },
}

/**
 * Values the screen works out for itself rather than reading from a table.
 *
 * These are the honest answer to "where does this come from" for anything
 * computed: the garden's height, the number of copies in a bag, how far
 * through a session you are. Labelling them `T8.copies` would be a lie —
 * there is no such column, deliberately (see `services/copies.py`).
 */
export const DERIVED = 'calc'

// ------------------------------------------------------------ the switch

type TraceState = { on: boolean; toggle: () => void }

const TraceContext = createContext<TraceState>({ on: false, toggle: () => {} })

const STORAGE_KEY = 'wordsprout.trace'

function initialSetting(): boolean {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.has('trace')) return params.get('trace') !== '0'
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    // Private browsing, or storage switched off. Not a reason to fail to
    // render the app.
    return false
  }
}

export function TraceProvider({ children }: { children: ReactNode }) {
  const [on, setOn] = useState(initialSetting)

  const toggle = useCallback(() => {
    setOn(previous => {
      const next = !previous
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch {
        /* nothing to do; the toggle still works for this page */
      }
      return next
    })
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 't') {
        event.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  const value = useMemo(() => ({ on, toggle }), [on, toggle])
  return <TraceContext.Provider value={value}>{children}</TraceContext.Provider>
}

export function useTrace(): TraceState {
  return useContext(TraceContext)
}

// ------------------------------------------------------------- the label

type Parsed =
  | { kind: 'table'; tid: string; table: string; column: string; colour: string }
  | { kind: 'derived'; note: string; colour: string }
  | { kind: 'unknown'; raw: string; colour: string }

const UNKNOWN = '#B02A2A'

export function parseTraceId(id: string): Parsed {
  const parts = id.split('.')
  const head = parts[0] ?? ''
  const tail = parts.slice(1).join('.')
  if (head === DERIVED) {
    return { kind: 'derived', note: tail || 'worked out on the page', colour: '#55636F' }
  }
  const entry = TABLES[head]
  if (!entry || !tail) return { kind: 'unknown', raw: id, colour: UNKNOWN }
  return {
    kind: 'table',
    tid: head,
    table: entry.table,
    column: tail,
    colour: GROUPS[entry.group].colour,
  }
}

/**
 * Wrap a value so the overlay can say where it came from.
 *
 * Renders exactly its children when the overlay is off, so leaving these in
 * place costs nothing at runtime beyond one context read.
 */
/** Open the data map at this table (and column), in a new tab. */
export function openDataMap(tid: string, column?: string): void {
  window.open(`/data-model.html#${tid}${column ? `.${column}` : ''}`, '_blank', 'noopener')
}

export function Trace({
  id, children, block = false, title, writes = false,
}: {
  /** `T8.successes`, or `calc.how many copies are in the bag` for a computed value. */
  id: string
  children?: ReactNode
  /** Lay the label above the value rather than beside it. */
  block?: boolean
  /** Extra explanation, shown on hover. */
  title?: string
  /** This is where something typed or tapped here is SAVED, not read from. */
  writes?: boolean
}) {
  const { on } = useTrace()
  if (!on) return <>{children}</>

  const parsed = parseTraceId(id)
  const arrow = writes ? '→ ' : ''
  const label =
    parsed.kind === 'table' ? `${arrow}${parsed.tid}.${parsed.column}`
    : parsed.kind === 'derived' ? `${arrow}calc`
    : `? ${parsed.raw}`
  const hover =
    parsed.kind === 'table'
      ? `${writes ? 'Saved to' : 'Read from'} ${parsed.table}.${parsed.column}${title ? ` — ${title}` : ''}. Click to open the data map.`
    : parsed.kind === 'derived' ? `${writes ? 'Saved after working out' : 'Worked out on the page'}: ${parsed.note}`
    : `No table called "${parsed.raw.split('.')[0]}" — check this label`

  return (
    <span
      title={hover}
      style={{
        display: block ? 'block' : 'inline-flex',
        alignItems: 'baseline',
        gap: 4,
        outline: `${writes ? 2 : 1}px ${writes ? 'solid' : 'dashed'} ${parsed.colour}`,
        outlineOffset: 1,
        borderRadius: 3,
        position: 'relative',
      }}
    >
      {children}
      <span
        role={parsed.kind === 'table' ? 'link' : undefined}
        onClick={parsed.kind === 'table' ? (e) => {
          e.stopPropagation(); e.preventDefault(); openDataMap(parsed.tid, parsed.column)
        } : undefined}
        style={{
          cursor: parsed.kind === 'table' ? 'pointer' : 'help',
          fontFamily: 'ui-monospace, Consolas, monospace',
          fontSize: 9,
          lineHeight: 1.3,
          fontWeight: 700,
          letterSpacing: 0,
          whiteSpace: 'nowrap',
          color: writes ? parsed.colour : '#fff',
          background: writes ? '#fff' : parsed.colour,
          border: writes ? `1.5px solid ${parsed.colour}` : 'none',
          borderRadius: 3,
          padding: '0 3px',
          verticalAlign: 'super',
          alignSelf: 'flex-start',
        }}
      >
        {label}
      </span>
    </span>
  )
}

/**
 * The same thing for a whole region — a card, a list, a screen.
 *
 * Use it when every value inside comes from one table and labelling each one
 * separately would bury the screen in badges.
 */
export function TraceRegion({
  id, children, note, writes = false,
}: {
  /**
   * A bare table number — `T14`, not `T14.trial`. A region names a whole
   * table, and writing a column name here that is really the table's own name
   * is the sort of label the overlay exists to stop people trusting.
   */
  id: string
  children: ReactNode
  note?: string
  /** Everything chosen inside is saved to this table. */
  writes?: boolean
}) {
  const { on } = useTrace()
  if (!on) return <>{children}</>

  const entry = TABLES[id]
  const parsed: Parsed = entry
    ? { kind: 'table', tid: id, table: entry.table, column: '', colour: GROUPS[entry.group].colour }
    : { kind: 'unknown', raw: id, colour: UNKNOWN }
  const name =
    parsed.kind === 'table' ? `${writes ? '→ saves to ' : ''}${parsed.tid} · ${parsed.table}`
    : `? ${id}`

  return (
    <div style={{
      outline: `${writes ? 2 : 1}px ${writes ? 'solid' : 'dashed'} ${parsed.colour}`,
      outlineOffset: 3,
      borderRadius: 6,
      position: 'relative',
      marginBottom: 10,
    }}>
      <div
        role={parsed.kind === 'table' ? 'link' : undefined}
        onClick={parsed.kind === 'table' ? () => openDataMap(parsed.tid) : undefined}
        title={`${name}${note ? ` — ${note}` : ''}. Click to open the data map at this table.`}
        style={{
        cursor: parsed.kind === 'table' ? 'pointer' : 'default',
        position: 'absolute', top: -9, left: 8, zIndex: 1,
        fontFamily: 'ui-monospace, Consolas, monospace',
        maxWidth: 'calc(100% - 16px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        fontSize: 9, fontWeight: 700, color: '#fff',
        background: parsed.colour, borderRadius: 3, padding: '1px 5px',
      }}>
        {name}{note ? ` — ${note}` : ''}
      </div>
      {children}
    </div>
  )
}
